import { useCallback, useEffect, useMemo, useState } from 'react';
import { campaignAccessAPI, rulesAPI } from '../services/api';

/**
 * Rules & Permissions for the signed-in admin, loaded from GET /api/v1/admin/rules/me
 * and shared by every component (one request, refreshed after rule changes).
 *
 *   can('campaign.create')            company-level action allowed?
 *   canAny('candidate.add')           allowed on at least some campaigns?
 *   canOn(campaign, 'campaign.edit')  allowed on this campaign? (uses campaign.allowed_actions
 *                                     from the campaigns API; falls back to canAny)
 *   policy('assign.default_access')   a company / team rule value
 *
 * The server enforces every rule; this only hides or disables buttons so people
 * aren't offered actions that would be refused.
 */
const EMPTY = { loaded: false, permissions: {}, policies: {}, roles: [], canEditCompanyRules: false, canEditTeamRules: false };

let state = EMPTY;
let inflight = null;
const listeners = new Set();

function publish(next) {
  state = next;
  listeners.forEach((fn) => fn(state));
}

export function refreshRules() {
  inflight = rulesAPI.me()
    .then((res) => publish({ ...EMPTY, ...res.data, loaded: true }))
    .catch(() => publish({ ...EMPTY, loaded: true, failed: true }))
    .finally(() => { inflight = null; });
  return inflight;
}

export function useRules() {
  const [me, setMe] = useState(state);

  useEffect(() => {
    listeners.add(setMe);
    if (!state.loaded && !inflight) refreshRules();
    return () => { listeners.delete(setMe); };
  }, []);

  return useMemo(() => {
    // Until loaded (or if the call failed) allow everything: the server still decides.
    const open = !me.loaded || me.failed;
    const scope = (key) => (open ? 'ALL' : me.permissions?.[key] || 'NONE');
    const can = (key) => scope(key) === 'ALL';
    const canAny = (key) => scope(key) !== 'NONE';
    const canOn = (campaign, key) => {
      if (open) return true;
      if (Array.isArray(campaign?.allowed_actions)) return campaign.allowed_actions.includes(key);
      return canAny(key);
    };
    const policy = (key) => me.policies?.[key];
    const policyOn = (key) => String(me.policies?.[key]) === 'true';
    const hasRole = (role) => Array.isArray(me.roles) && me.roles.includes(role);
    return { ...me, scope, can, canAny, canOn, policy, policyOn, hasRole, refresh: refreshRules };
  }, [me]);
}

/**
 * What the signed-in admin may do on one campaign: { allowedActions, myAccess, locked, can(key) }.
 * Until it loads, can() returns true so nothing flickers disabled; the server still enforces.
 */
export function useCampaignAccess(campaignId) {
  const [access, setAccess] = useState(null);
  const load = useCallback(() => {
    if (!campaignId) { setAccess(null); return; }
    campaignAccessAPI.myAccess(campaignId)
      .then((res) => setAccess(res.data))
      .catch(() => setAccess(null));
  }, [campaignId]);
  useEffect(() => { load(); }, [load]);
  return useMemo(() => ({
    ...(access || {}),
    loaded: !!access,
    reload: load,
    can: (key) => !access || (Array.isArray(access.allowedActions) && access.allowedActions.includes(key)),
  }), [access, load]);
}

/** The server's "why not" message from a 403 / 400 response. */
export function permissionError(err, fallback) {
  const detail = err?.response?.data?.detail;
  return typeof detail === 'string' ? detail : fallback;
}
